import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { InfoCard } from "@/components/toggles";
import { useAuth } from "@/contexts/AuthContext";
import { useCatalogLabels } from "@/i18n/catalog";
import api from "@/lib/api";
import RichTextView from "@/components/common/RichTextView";
import SellerRatingBadge from "@/components/reviews/SellerRatingBadge";
import ReviewsList from "@/components/reviews/ReviewsList";
import BuyerTryOnModal from "@/components/BuyerTryOnModal";
import BodyBlurredImage from "@/components/media/BodyBlurredImage";
import AVSBlurBadge from "@/components/media/AVSBlurBadge";
import STXThemeToggle from "@/components/stx/STXThemeToggle";
import ScarcityNudge from "@/components/marketplace/ScarcityNudge";
import {
  ArrowLeft, Heart, Lock, ShoppingBag, ShoppingCart, Sparkles,
  User, Ruler, Weight, Clock, ChevronLeft, ChevronRight, Check,
} from "lucide-react";

// STX-Kategorie-Codes → DE-Labels (Fallback für DE-Version)
const STX_CATEGORY_LABELS_DE = {
  socks: "Socken",
  slip: "Slips",
  bras: "BHs",
  stockings: "Strümpfe",
  lingerie: "Dessous",
  sportswear: "Sportswear",
};

const SEGMENT_LABELS = {
  FEMALE: "Damen",
  MALE: "Herren",
  DIVERSE: "Divers",
};

export default function STXDetailPage() {
  const { t } = useTranslation();
  const { addon: addonLabel } = useCatalogLabels();
  const { listingId } = useParams();
  const navigate = useNavigate();
  const { user } = useAuth();
  const [listing, setListing] = useState(null);
  // iter172n · Zentraler Frontend-Blur-Look (synchron mit Admin-Testtool)
  const [blurCfg, setBlurCfg] = useState({ blur_strength: 32, blur_field_expansion: 0.06 });
  const [textData, setTextData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [imgIdx, setImgIdx] = useState(0);
  const [showGuestCheckout, setShowGuestCheckout] = useState(false);
  const [showCheckout, setShowCheckout] = useState(false);
  const [showTryOn, setShowTryOn] = useState(false);
  const [guestEmail, setGuestEmail] = useState("");
  const [guestName, setGuestName] = useState("");
  const [guestAddress, setGuestAddress] = useState("");
  const [guestCity, setGuestCity] = useState("");
  const [guestZip, setGuestZip] = useState("");
  const [shipName, setShipName] = useState("");
  const [shipAddress, setShipAddress] = useState("");
  const [shipCity, setShipCity] = useState("");
  const [shipZip, setShipZip] = useState("");
  const [purchasing, setPurchasing] = useState(false);
  const [orderDone, setOrderDone] = useState(null);
  const [addingToCart, setAddingToCart] = useState(false);
  const [selectedAddonCodes, setSelectedAddonCodes] = useState([]);
  const [selectedWearDays, setSelectedWearDays] = useState(null);
  const [addonLabels, setAddonLabels] = useState({});
  // Salmon-Pulse Interaction-Feedback (Enhancement iter88.15b)
  const [pulseCode, setPulseCode] = useState(null);      // addon-code oder "wear-<days>"
  const [heartPulse, setHeartPulse] = useState(false);
  const [sellerMore, setSellerMore] = useState([]);

  const triggerPulse = (key) => {
    setPulseCode(key);
    setTimeout(() => setPulseCode((cur) => (cur === key ? null : cur)), 260);
  };

  useEffect(() => {
    (async () => {
      try {
        const [listRes, textRes, addonRes, cfgRes] = await Promise.all([
          api.get(`/listings/${listingId}`),
          api.get(`/listings/${listingId}/text`),
          api.get(`/stilltouchedx/catalog/addons`),
          api.get(`/body-detection/render-config`).catch(() => ({ data: null })),
        ]);
        setListing(listRes.data);
        setTextData(textRes.data);
        const map = {};
        for (const a of (addonRes.data.addons || [])) map[a.code] = a.label;
        setAddonLabels(map);
        if (cfgRes?.data) setBlurCfg(cfgRes.data);
      } catch { setListing(null); }
      finally { setLoading(false); }
    })();
  }, [listingId]);

  // iter119: "Mehr vom Verkäufer" — nur laden, wenn seller.handle bekannt.
  useEffect(() => {
    if (!listing?.seller?.handle) return;
    (async () => {
      try {
        const { data } = await api.get(`/profiles/${listing.seller.handle}/listings?area=STILLTOUCHEDX&limit=6`);
        const others = (data?.listings || []).filter((l) => l.listing_id !== listingId);
        setSellerMore(others.slice(0, 4));
      } catch { setSellerMore([]); }
    })();
  }, [listing?.seller?.handle, listingId]);

  const handleAddToCart = async () => {
    if (!user || user === false) { navigate("/login?next=/stilltouchedx/" + listingId); return; }
    setAddingToCart(true);
    try {
      await api.post("/cart/items", {
        listing_id: listing.listing_id,
        quantity: 1,
        selected_addon_codes: selectedAddonCodes,
        selected_wear_days: selectedWearDays,
      });
      window.dispatchEvent(new Event("cart-refresh"));
      setAddingToCart("done");
      setTimeout(() => setAddingToCart(false), 1400);
    } catch (e) {
      alert(e.response?.data?.detail || t("mp_detail.add_cart_failed"));
      setAddingToCart(false);
    }
  };

  const toggleWatch = async () => {
    if (!user || user === false) { navigate("/login"); return; }
    try {
      const { data } = await api.post(`/listings/${listingId}/watch`);
      setListing((prev) => ({ ...prev, is_watched: data.watched }));
      if (data.watched) {
        setHeartPulse(true);
        setTimeout(() => setHeartPulse(false), 280);
      }
    } catch { /* ignore */ }
  };

  const handleGuestCheckout = async () => {
    if (!guestEmail || !guestName || !guestAddress || !guestCity || !guestZip) return;
    setPurchasing(true);
    try {
      const { data } = await api.post("/orders/guest-checkout", {
        listing_id: listingId, email: guestEmail, guest_name: guestName,
        shipping_address: guestAddress, shipping_city: guestCity, shipping_zip: guestZip,
      });
      setOrderDone(data);
    } catch { /* ignore */ }
    finally { setPurchasing(false); }
  };

  const handleLoggedInCheckout = async () => {
    if (!shipName || !shipAddress || !shipCity || !shipZip) return;
    setPurchasing(true);
    try {
      const { data } = await api.post("/orders/checkout", {
        listing_id: listingId, shipping_name: shipName,
        shipping_address: shipAddress, shipping_city: shipCity, shipping_zip: shipZip,
      });
      setOrderDone(data);
    } catch { /* ignore */ }
    finally { setPurchasing(false); }
  };

  if (loading) {
    return (
      <div className="min-h-full bg-stx-bg flex justify-center py-16">
        <div className="w-6 h-6 border-2 border-stx-border border-t-stx-accent rounded-full animate-spin" />
      </div>
    );
  }
  if (!listing) {
    return <div className="min-h-full bg-stx-bg px-4 py-16 text-center text-sm text-stx-text-dim">{t("mp_detail.not_found")}</div>;
  }

  if (orderDone) {
    return (
      <div data-testid="stx-order-done" className="min-h-full bg-stx-bg text-stx-text flex flex-col items-center justify-center px-6 py-16">
        <div className="w-14 h-14 rounded-full bg-stx-accent-soft flex items-center justify-center mb-4">
          <ShoppingBag size={26} className="text-stx-accent" />
        </div>
        <h2 className="text-lg font-bold">{t("mp_detail.order_placed")}</h2>
        <p className="text-sm text-stx-text-muted mt-2 text-center">
          Tracking-Token: <span className="text-stx-text font-mono text-xs">{orderDone.guest_tracking_token || orderDone.order_id}</span>
        </p>
        <button data-testid="stx-order-back" onClick={() => navigate("/stilltouchedx")}
          className="mt-6 h-11 px-6 rounded-full bg-stx-accent text-stx-accent-fg text-sm font-medium hover:bg-stx-accent-hover transition-colors">
          Weiter stöbern
        </button>
      </div>
    );
  }

  const seller = listing.seller || {};
  const facets = listing.seller_facets || {};
  const avsStatus = user?.age_verification_status || "NOT_STARTED";
  const isAvsVerified = avsStatus === "VERIFIED";
  const isAvsPending = avsStatus === "PENDING";
  const isAvsRejected = avsStatus === "REJECTED";
  const mediaList = listing.media_urls || [];
  const wornList = listing.worn_photo_urls || [];
  const bodyContentList = listing.body_content_urls || [];
  const regionsMap = listing.body_regions_by_url || {}; // iter172d
  const wornSet = new Set(wornList);
  const bodyContentSet = new Set(bodyContentList);
  const gallery = [
    ...mediaList.map((url) => ({
      url,
      isWorn: false,
      hasBodyContent: bodyContentSet.has(url) || wornSet.has(url),
      regions: regionsMap[url] || [],
    })),
    ...wornList
      .filter((u) => !mediaList.includes(u))
      .map((url) => ({ url, isWorn: true, hasBodyContent: true, regions: regionsMap[url] || [] })),
  ];
  const titleUrl = listing.title_photo_url || gallery[0]?.url;
  const sortedGallery = gallery.slice().sort((a, b) => (a.url === titleUrl ? -1 : b.url === titleUrl ? 1 : 0));
  const currentImg = sortedGallery[Math.min(imgIdx, sortedGallery.length - 1)] || sortedGallery[0];
  // iter172c · Nur blurren wenn dieses konkrete Bild Körper-Inhalt hat UND User kein AVS
  const isCurrentBlurred = !!currentImg?.hasBodyContent && !isAvsVerified;
  const desc = textData?.description || listing.description;
  const viewMode = textData?.view_mode || "PUBLIC_MAPPED";

  const nextImg = () => setImgIdx((i) => (i + 1) % Math.max(1, sortedGallery.length));
  const prevImg = () => setImgIdx((i) => (i - 1 + sortedGallery.length) % Math.max(1, sortedGallery.length));

  // Preisberechnung inkl. Extras
  const basePrice = Number(listing.price || 0);
  const addonSum = (listing.stx_addon_offers || [])
    .filter((o) => selectedAddonCodes.includes(o.code))
    .reduce((s, o) => s + Number(o.price || 0), 0);
  const wearSum = selectedWearDays != null
    ? Number((listing.stx_extra_wear_offers || []).find((o) => o.days === selectedWearDays)?.price || 0)
    : 0;
  const totalPrice = basePrice + addonSum + wearSum;
  const currency = listing.currency || "EUR";

  return (
    <div data-testid="stx-detail-page" className="bg-stx-bg min-h-full text-stx-text pb-28 w-full max-w-3xl md:max-w-6xl 2xl:max-w-full mx-auto 2xl:mx-0">
      {/* Top Bar */}
      <div className="sticky top-0 z-40 flex items-center gap-3 px-4 py-2.5 bg-stx-bg/95 backdrop-blur border-b border-stx-border">
        <button data-testid="stx-detail-back" onClick={() => navigate(-1)}
          className="w-9 h-9 -ml-1.5 flex items-center justify-center rounded-full text-stx-text-muted hover:text-stx-accent hover:bg-stx-accent-soft transition-colors">
          <ArrowLeft size={20} />
        </button>
        <span className="text-[11px] font-mono uppercase tracking-wider text-stx-text-dim flex-1 truncate">
          {listing.public_item_no}
        </span>
        <STXThemeToggle />
        <button data-testid="stx-detail-watch" onClick={toggleWatch}
          className="w-9 h-9 flex items-center justify-center rounded-full hover:bg-stx-accent-soft transition-colors">
          <Heart size={18} className={`${listing.is_watched ? "fill-stx-accent text-stx-accent" : "text-stx-text-muted"} ${heartPulse ? "stx-heart-pop" : ""}`} />
        </button>
      </div>

      {/* iter118: 2-Column-Split ab md */}
      <div className="md:grid md:grid-cols-2 md:gap-6 md:px-4 md:pt-4 md:items-start">

      {/* iter172k · Sticky-Wrapper: Hero + Thumbnails teilen sich ein Grid-Item,
          sodass Sticky innerhalb der Gesamt-Row-Höhe (rechte Spalte = Info) rutschen kann. */}
      <div className="md:sticky md:top-4 md:self-start md:h-fit">

      {/* Hero-Galerie */}
      <div className="relative aspect-[4/5] bg-stx-panel mx-3 mt-3 md:mx-0 md:mt-0 rounded-2xl overflow-hidden">
        {sortedGallery.length > 0 ? (
          <>
            {isCurrentBlurred && (!currentImg.regions || currentImg.regions.length === 0) ? (
              // Explizit erkannt, aber keine serverseitig servierbaren Regionen →
              // sicherer Vollbild-Blur (konsistent zum STX-Feed: blur-2xl + 18+-Badge).
              <img
                data-testid="stx-detail-image"
                src={currentImg.url}
                alt=""
                className="w-full h-full object-cover bg-stx-panel blur-2xl scale-110 pointer-events-none select-none"
                draggable={false}
              />
            ) : (
              <BodyBlurredImage
                listingId={listing.listing_id}
                rawUrl={currentImg.url}
                hasRegions={!!(currentImg.hasBodyContent && currentImg.regions && currentImg.regions.length > 0)}
                isViewerAvsVerified={isAvsVerified || listing.seller_profile_id === user?._id}
                cacheBust={blurCfg.config_version}
                alt=""
                className="w-full h-full object-cover bg-stx-panel"
                testId="stx-detail-image"
              />
            )}
            {/* iter309 · Vollbild-Blur-Fallback (keine Regionen): einheitlicher 18+-Marker. */}
            {isCurrentBlurred && (!currentImg.regions || currentImg.regions.length === 0) && (
              <AVSBlurBadge size="lg" testId="stx-detail-avs-badge" />
            )}
          </>
        ) : (
          <div className="w-full h-full flex items-center justify-center">
            <Lock size={40} className="text-stx-text-dim" />
          </div>
        )}

        {sortedGallery.length > 1 && !(isCurrentBlurred && (!currentImg.regions || currentImg.regions.length === 0)) && (
          <>
            <button onClick={prevImg} aria-label="Voriges Bild"
              data-testid="stx-detail-prev"
              className="absolute left-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-stx-bg/70 backdrop-blur border border-stx-border text-stx-text hover:text-stx-accent hover:border-stx-accent transition-colors flex items-center justify-center">
              <ChevronLeft size={18} />
            </button>
            <button onClick={nextImg} aria-label={t("mp_detail.next_image")}
              data-testid="stx-detail-next"
              className="absolute right-2 top-1/2 -translate-y-1/2 w-9 h-9 rounded-full bg-stx-bg/70 backdrop-blur border border-stx-border text-stx-text hover:text-stx-accent hover:border-stx-accent transition-colors flex items-center justify-center">
              <ChevronRight size={18} />
            </button>
          </>
        )}

        {sortedGallery.length > 1 && (
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex gap-1.5" data-testid="stx-detail-dots">
            {sortedGallery.map((_, i) => (
              <button key={i} type="button" data-testid={`stx-detail-dot-${i}`}
                onClick={() => setImgIdx(i)}
                aria-label={`Bild ${i + 1}`}
                className={`h-1.5 rounded-full transition-all ${i === imgIdx ? "w-6 bg-stx-accent" : "w-1.5 bg-stx-text/40"}`} />
            ))}
          </div>
        )}

        {isCurrentBlurred && (!currentImg.regions || currentImg.regions.length === 0) && (
          <div className="absolute inset-0 flex flex-col items-center justify-center gap-3 px-6 text-center">
            <Lock size={32} className="text-stx-accent" />
            {!user ? (
              <>
                <p className="text-xs text-stx-text-muted">{t("mp_detail.login_for_worn_photos")}</p>
                <button onClick={() => navigate("/login")}
                  className="px-4 py-2 rounded-full bg-stx-accent text-stx-accent-fg text-xs font-medium hover:bg-stx-accent-hover transition-colors">
                  Anmelden
                </button>
              </>
            ) : isAvsPending ? (
              <>
                <p className="text-xs text-amber-400 font-medium">{t("stx_detail_extra.avs_pending_title")}</p>
                <p className="text-[10px] text-stx-text-muted">{t("stx_detail_extra.avs_pending_body")}</p>
              </>
            ) : isAvsRejected ? (
              <>
                <p className="text-xs text-red-400 font-medium">AVS-Verifizierung abgelehnt</p>
                <p className="text-[10px] text-stx-text-muted">{t("mp_detail.contact_support")}</p>
              </>
            ) : (
              <>
                <p className="text-xs text-stx-text-muted">{t("mp_detail.verify_age_hint")}</p>
                <button data-testid="avs-start-btn" onClick={async () => {
                  try {
                    await api.post("/avs/request", {});
                    alert("AVS-Verifizierung beantragt. Du wirst benachrichtigt.");
                  } catch (err) {
                    const msg = err?.response?.data?.detail || err?.response?.data?.message || "Fehler";
                    alert(msg);
                  }
                }} className="px-4 py-2 rounded-full bg-stx-accent text-stx-accent-fg text-xs font-medium hover:bg-stx-accent-hover transition-colors">
                  AVS Verifizierung starten
                </button>
              </>
            )}
          </div>
        )}
      </div>

      {/* Thumbnail-Row */}
      {sortedGallery.length > 1 && (
        <div data-testid="stx-detail-thumbs" className="flex gap-2 px-4 py-3 overflow-x-auto bg-stx-bg scrollbar-hide">
          {sortedGallery.map((item, i) => {
            // iter172c · Blur/Lock pro-Thumb nur wenn Bild wirklich Körper-Inhalt hat
            const thumbBlurred = !!item.hasBodyContent && !isAvsVerified;
            const isSelected = i === imgIdx;
            return (
              <button
                key={item.url}
                type="button"
                data-testid={`stx-detail-thumb-${i}`}
                onClick={() => setImgIdx(i)}
                className={`relative flex-shrink-0 w-16 h-20 rounded-md overflow-hidden border-2 transition-all ${
                  isSelected ? "border-stx-accent" : "border-transparent hover:border-stx-border-strong"
                }`}>
                <img src={item.url} alt="" className={`w-full h-full object-cover ${thumbBlurred ? "blur-2xl scale-110" : ""}`} />
                {item.isWorn && (
                  <span className="absolute top-0.5 left-0.5 text-[8px] bg-stx-accent text-stx-accent-fg px-1 py-0.5 uppercase tracking-wider rounded-sm">Trage</span>
                )}
                {thumbBlurred && (
                  <div className="absolute inset-0 flex items-center justify-center">
                    <Lock size={14} className="text-stx-text/80" />
                  </div>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* iter172k · Ende Sticky-Wrapper (Hero + Thumbnails) */}
      </div>

      {/* iter118: Info-Spalte (rechts ab md) */}
      <div className="md:min-w-0">

      {/* Titel + Preis */}
      <div className="px-5 pt-5 pb-4 md:px-0 md:pt-0">
        {/* iter171 · Kategorie/Segment/AVS-Badges entfernt (kein Mehrwert für User).
            Rescue-Phase-Badge bleibt (relevanter Preisstatus). */}
        {listing.rescue_phase === "RESCUE" && (
          <div className="mb-2.5">
            <span
              data-testid="stx-detail-rescue-phase"
              className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-semibold uppercase tracking-wider ${
                (listing.rescue_phase_ends_in_days ?? 99) <= 3
                  ? "bg-red-500/15 text-red-400 border border-red-500/30"
                  : "bg-emerald-500/15 text-emerald-400 border border-emerald-500/30"
              }`}
            >
              {(listing.rescue_phase_ends_in_days ?? 99) <= 3 ? "Letzte Chance" : "Preis reduziert"}
            </span>
          </div>
        )}

        <h1 className="text-2xl font-bold leading-tight text-stx-text">{listing.title}</h1>

        <div className="mt-3 flex items-baseline gap-2">
          <p className="text-3xl font-bold text-stx-accent">{basePrice.toFixed(2)}</p>
          <span className="text-sm text-stx-text-muted">{currency}</span>
          {(addonSum + wearSum) > 0 && (
            <span data-testid="stx-detail-total-hint" className="ml-auto text-[11px] text-stx-text-muted">
              Gesamt inkl. Extras: <span className="text-stx-text font-semibold">{totalPrice.toFixed(2)} {currency}</span>
            </span>
          )}
        </div>
      </div>

      {/* Seller-Card */}
      <div className="mx-5 mb-5 rounded-xl bg-stx-panel border border-stx-border overflow-hidden">
        <button
          data-testid="stx-seller-link"
          onClick={() => seller.handle && navigate(`/user/${seller.handle}/stx`)}
          disabled={!seller.handle}
          className="flex items-center gap-3 w-full text-left px-4 py-3 disabled:cursor-default hover:bg-stx-panel-2 transition-colors"
        >
          <div className="w-11 h-11 rounded-full bg-stx-panel-2 flex items-center justify-center text-sm font-bold text-stx-text-muted overflow-hidden shrink-0 border border-stx-border">
            {seller.avatar_url ? (
              <img src={seller.avatar_url} alt="" className="w-full h-full object-cover" />
            ) : (
              (seller.display_name || "?")[0]?.toUpperCase()
            )}
          </div>
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold text-stx-text truncate" data-testid="stx-seller-name">
              {seller.display_name || "—"}
            </p>
            {listing.seller_profile_id && (
              <SellerRatingBadge sellerId={listing.seller_profile_id} area="STX" className="mt-0.5" />
            )}
            {seller.handle && <p className="text-[10px] text-stx-accent mt-0.5">{t("public_profile.view_profile_arrow")}</p>}
          </div>
        </button>

        {Object.keys(facets).length > 1 && (
          <div data-testid="seller-facets" className="flex gap-2 px-4 pb-3 flex-wrap">
            {facets.age_range && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-stx-bg border border-stx-border text-[10px] text-stx-text-muted">
                <User size={10} /> {facets.age_range}
              </span>
            )}
            {facets.height_cm && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-stx-bg border border-stx-border text-[10px] text-stx-text-muted">
                <Ruler size={10} /> {facets.height_cm} cm
              </span>
            )}
            {facets.weight_kg && (
              <span className="inline-flex items-center gap-1 px-2 py-1 rounded-md bg-stx-bg border border-stx-border text-[10px] text-stx-text-muted">
                <Weight size={10} /> {facets.weight_kg} kg
              </span>
            )}
            {facets.body_type && (() => {
              const legacy = { "Schlank": "SLIM", "Sportlich": "SPORTY", "Normal": "NORMAL", "Kurvig": "CURVY", "Kräftig": "STRONG", "Athletisch": "SPORTY" };
              const code = legacy[facets.body_type] || facets.body_type;
              const fallback = { SLIM: "Schlank", SPORTY: "Sportlich", NORMAL: "Normal", CURVY: "Kurvig", STRONG: "Kräftig" }[code] || facets.body_type;
              return (
                <span className="px-2 py-1 rounded-md bg-stx-bg border border-stx-border text-[10px] text-stx-text-muted">
                  {t(`facets.body_type_options.${code}`, { defaultValue: fallback })}
                </span>
              );
            })()}
            {facets.shoe_size && (
              <span className="px-2 py-1 rounded-md bg-stx-bg border border-stx-border text-[10px] text-stx-text-muted">{t("facets.shoe_size", { defaultValue: "Schuh" })} {facets.shoe_size}</span>
            )}
          </div>
        )}
      </div>

      {/* Beschreibung + Bewertungen wandern iter119 aus der Info-Spalte in einen
          full-width Block direkt unter der Gallery (siehe unten). */}

      {/* Add-Ons — iter309 · nur für AVS-verifizierte sichtbar/kaufbar.
          Non-AVS: Hinweis + AVS-Link statt der Optionen (Kauf & Tragetage bleiben). */}
      {Array.isArray(listing.stx_addon_offers) && listing.stx_addon_offers.length > 0 && (
        isAvsVerified ? (
        <div data-testid="stx-detail-addons" className="px-5 py-5 border-t border-stx-border">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em]">
              {t("mp_detail.addons_title")}
            </h3>
            <span className="text-[10px] text-stx-text-dim">{t("mp_detail.addons_multi_select")}</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {listing.stx_addon_offers.map((o) => {
              const active = selectedAddonCodes.includes(o.code);
              return (
                <button
                  key={o.code}
                  data-testid={`stx-detail-addon-${o.code}`}
                  onClick={() => {
                    setSelectedAddonCodes((prev) => active ? prev.filter((c) => c !== o.code) : [...prev, o.code]);
                    triggerPulse(o.code);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-left
                    ${active
                      ? "bg-stx-accent-soft border-stx-accent"
                      : "bg-stx-panel border-stx-border hover:border-stx-border-strong"}
                    ${pulseCode === o.code ? "stx-pulse" : ""}`}
                >
                  <span className="inline-flex items-center gap-3 text-sm">
                    <span className={`w-5 h-5 rounded-md flex items-center justify-center border transition-colors
                      ${active ? "bg-stx-accent border-stx-accent" : "bg-transparent border-stx-border-strong"}`}>
                      {active && <Check size={12} className="text-stx-accent-fg" strokeWidth={3} />}
                    </span>
                    <span className={active ? "text-stx-text font-medium" : "text-stx-text"}>
                      {addonLabel(o.code, addonLabels[o.code] || o.code.replace(/^OPT_/, "").replaceAll("_", " ").toLowerCase())}
                    </span>
                  </span>
                  <span className={`text-sm font-bold tabular-nums ${active ? "text-stx-accent" : "text-stx-text-muted"}`}>
                    +{Number(o.price).toFixed(2)} {currency}
                  </span>
                </button>
              );
            })}
          </div>
          <p className="text-[10px] text-stx-text-dim mt-3">
            {t("mp_detail.addons_hint")}
          </p>
        </div>
        ) : (
        <div data-testid="stx-detail-addons-avs-gate" className="px-5 py-5 border-t border-stx-border">
          <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em] mb-3">
            {t("mp_detail.addons_title")}
          </h3>
          <div className="rounded-xl border border-stx-border bg-stx-panel p-4 flex flex-col gap-3">
            <div className="flex items-start gap-2.5">
              <Lock size={16} className="text-stx-accent mt-0.5 shrink-0" />
              <p className="text-xs text-stx-text-muted leading-relaxed">
                {t("stx_detail_extra.addons_avs_needed", {
                  defaultValue: "AVS nötig, um die Zusatz-Optionen zu sehen und zu kaufen. Verifiziere dein Alter, um alle Extras dieses Artikels freizuschalten.",
                })}
              </p>
            </div>
            <button
              data-testid="stx-detail-addons-avs-btn"
              onClick={() => navigate("/profile/security")}
              className="self-start px-4 py-2 rounded-full bg-stx-accent text-stx-accent-fg text-xs font-medium hover:bg-stx-accent-hover transition-colors">
              {t("mp_detail.verify_age_hint", { defaultValue: "AVS-Verifizierung starten" })}
            </button>
          </div>
        </div>
        )
      )}

      {/* Extra-Tragetage */}
      {Array.isArray(listing.stx_extra_wear_offers) && listing.stx_extra_wear_offers.length > 0 && (
        <div data-testid="stx-detail-weardays" className="px-5 py-5 border-t border-stx-border">
          <div className="flex items-baseline justify-between mb-3">
            <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em]">
              {t("mp_detail.extra_wear_title")}
            </h3>
            <span className="text-[10px] text-stx-text-dim">{t("mp_detail.extra_wear_single_select")}</span>
          </div>
          <div className="grid grid-cols-1 gap-2">
            {listing.stx_extra_wear_offers.map((o) => {
              const active = selectedWearDays === o.days;
              return (
                <button
                  key={o.days}
                  data-testid={`stx-detail-weardays-${o.days}`}
                  onClick={() => {
                    setSelectedWearDays(active ? null : o.days);
                    if (!active) triggerPulse(`wear-${o.days}`);
                  }}
                  className={`w-full flex items-center justify-between px-4 py-3 rounded-xl border transition-all text-left
                    ${active
                      ? "bg-stx-accent-soft border-stx-accent"
                      : "bg-stx-panel border-stx-border hover:border-stx-border-strong"}
                    ${pulseCode === `wear-${o.days}` ? "stx-pulse" : ""}`}
                >
                  <span className="inline-flex items-center gap-3 text-sm">
                    <span className={`w-5 h-5 rounded-full flex items-center justify-center border transition-colors
                      ${active ? "bg-stx-accent border-stx-accent" : "bg-transparent border-stx-border-strong"}`}>
                      {active && <span className="w-2 h-2 rounded-full bg-stx-accent-fg" />}
                    </span>
                    <span className="text-stx-text">{o.days === 1 ? t("mp_detail.extra_wear_days_one") : t("mp_detail.extra_wear_days_other", { n: o.days })}</span>
                  </span>
                  <span className={`text-sm font-bold tabular-nums ${active ? "text-stx-accent" : "text-stx-text-muted"}`}>
                    +{Number(o.price).toFixed(2)} {currency}
                  </span>
                </button>
              );
            })}
            {selectedWearDays !== null && (
              <button data-testid="stx-detail-weardays-clear"
                onClick={() => setSelectedWearDays(null)}
                className="text-[10px] text-stx-accent underline self-start mt-1">
                Auswahl entfernen
              </button>
            )}
          </div>
        </div>
      )}

      {/* Meta-Zeile */}
      <div className="px-5 py-3 border-t border-stx-border flex items-center gap-4 text-xs text-stx-text-dim">
        <span className="inline-flex items-center gap-1.5">
          <Heart size={12} /> {t("mp_detail.watchers", { count: listing.watch_count || 0 })}
        </span>
        {Number(listing.effective_processing_delay_days) > 0 && (
          <span data-testid="stx-detail-processing-delay" className="inline-flex items-center gap-1.5 text-amber-400">
            <Clock size={12} /> +{listing.effective_processing_delay_days} {listing.effective_processing_delay_days === 1 ? t("mp_detail.day_one") : t("mp_detail.day_other")} {t("mp_detail.processing_word")}
          </span>
        )}
      </div>

      {/* Logged-in Checkout */}
      {showCheckout && user && user !== false && (
        <div data-testid="stx-checkout-form" className="px-5 py-4 border-t border-stx-border space-y-3">
          <p className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em]">Versanddaten</p>
          <input data-testid="stx-ship-name" value={shipName} onChange={(e) => setShipName(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="Name *" />
          <input data-testid="stx-ship-address" value={shipAddress} onChange={(e) => setShipAddress(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder={t("mp_detail.address_star")} />
          <div className="flex gap-2">
            <input data-testid="stx-ship-zip" value={shipZip} onChange={(e) => setShipZip(e.target.value)}
              className="w-24 h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="PLZ *" />
            <input data-testid="stx-ship-city" value={shipCity} onChange={(e) => setShipCity(e.target.value)}
              className="flex-1 h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="Stadt *" />
          </div>
          <button data-testid="stx-checkout-submit" onClick={handleLoggedInCheckout} disabled={purchasing || !shipName || !shipAddress}
            className="w-full h-11 rounded-full bg-stx-accent text-stx-accent-fg text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-stx-accent-hover transition-colors disabled:opacity-30">
            <ShoppingBag size={14} /> {purchasing ? t("mp_detail.ordering") : t("mp_detail.buy_now")}
          </button>
        </div>
      )}

      {/* Guest Checkout */}
      {showGuestCheckout && (
        <div data-testid="guest-checkout-form" className="px-5 py-4 border-t border-stx-border space-y-3">
          <p className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em]">{t("mp_detail.guest_buy")}</p>
          <input data-testid="guest-email" value={guestEmail} onChange={(e) => setGuestEmail(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="E-Mail *" />
          <input data-testid="guest-name" value={guestName} onChange={(e) => setGuestName(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="Name *" />
          <input data-testid="guest-address" value={guestAddress} onChange={(e) => setGuestAddress(e.target.value)}
            className="w-full h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder={t("mp_detail.address_star")} />
          <div className="flex gap-2">
            <input data-testid="guest-zip" value={guestZip} onChange={(e) => setGuestZip(e.target.value)}
              className="w-24 h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="PLZ *" />
            <input data-testid="guest-city" value={guestCity} onChange={(e) => setGuestCity(e.target.value)}
              className="flex-1 h-11 px-3 rounded-lg bg-stx-panel border border-stx-border text-stx-text text-sm focus:outline-none focus:border-stx-accent placeholder:text-stx-text-dim" placeholder="Stadt *" />
          </div>
          <button data-testid="guest-checkout-submit" onClick={handleGuestCheckout} disabled={purchasing || !guestEmail || !guestName || !guestAddress}
            className="w-full h-11 rounded-full bg-stx-accent text-stx-accent-fg text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-stx-accent-hover transition-colors disabled:opacity-30">
            <ShoppingBag size={14} /> {purchasing ? t("mp_detail.ordering") : t("mp_detail.order_now")}
          </button>
        </div>
      )}

      {/* Try-On CTA */}
      {listing?.preview_config?.try_on_enabled && (
        <button
          data-testid="stx-detail-try-on-btn"
          onClick={() => {
            if (!user || user === false) { navigate("/login?next=/stilltouchedx/" + listing.listing_id); return; }
            setShowTryOn(true);
          }}
          className="mx-5 mt-5 mb-2 flex items-center gap-2 w-[calc(100%-2.5rem)] px-4 py-3 rounded-xl border border-stx-border bg-stx-panel text-stx-text hover:border-stx-accent hover:bg-stx-accent-soft active:scale-[0.98] transition">
          <Sparkles size={14} className="text-stx-accent" />
          <span className="text-xs">Virtuell anprobieren · KI-Vorschau</span>
        </button>
      )}
      {showTryOn && <BuyerTryOnModal listingId={listing.listing_id} onClose={() => setShowTryOn(false)} />}

      {/* iter172k · Beschreibung + Bewertungen in die Info-Spalte (rechts ab md),
          damit die rechte Spalte länger wird als der Sticky-Hero-Wrapper links
          und Sticky-Scroll-Room entsteht. Mehr vom Verkäufer wurde in iter172l
          unter die CTA-Buttons verschoben (User-Vorgabe). */}
      <div className="md:pt-2">
        {/* Beschreibung */}
        <div className="px-5 py-4 md:px-0 border-t border-stx-border">
          <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em] mb-2.5">{t("mp_detail.description")}</h3>
          <RichTextView html={desc} className="text-sm text-stx-text-muted leading-relaxed prose prose-sm max-w-none" />
        </div>

        {/* Bewertungen (iter88.21) — nur STX-Reviews sortenrein */}
        <div className="px-5 py-4 md:px-0 border-t border-stx-border" data-testid="stx-detail-reviews">
          <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em] mb-2.5">{t("seller_profile.reviews")}</h3>
          <ReviewsList scope="listing" scopeId={listing.listing_id} area="STX" limit={5} emptyText={t("mp_detail.no_review")} />
        </div>
      </div>

      </div>
      {/* /Info-Spalte */}
      </div>
      {/* /2-Column-Split */}

      {/* iter140 · Scarcity-Nudge (nur bei Unikaten sichtbar) */}
      <div className="max-w-3xl mx-auto px-4 md:px-6 mt-3">
        <ScarcityNudge listingId={listing.listing_id} theme="stx" />
      </div>

      {/* Bottom CTA — fixed am Handy, inline unter Info ab md */}
      <div className="fixed bottom-16 left-0 right-0 max-w-md mx-auto bg-stx-bg/95 backdrop-blur border-t border-stx-border px-4 py-3 flex gap-2 z-40 md:static md:max-w-3xl md:mt-4 md:border md:rounded-2xl md:backdrop-blur-0 md:bg-stx-panel 2xl:bottom-4">
        {listing.seller_profile_id && user && listing.seller_profile_id === user._id ? (
          <div data-testid="stx-detail-own-listing-notice" className="flex-1 h-11 rounded-full border border-stx-border text-stx-text/60 text-xs font-medium flex items-center justify-center px-3 text-center">
            {t("mp_detail.own_listing")}
          </div>
        ) : (
          <>
        <button data-testid="stx-detail-inquiry"
          onClick={() => {
            if (!user || user === false) { navigate("/login"); return; }
            navigate(`/stilltouchedx/presale-wizard?seller=${listing.seller_profile_id}&listing=${listing.listing_id}`);
          }}
          className="flex-1 h-11 rounded-full border border-stx-border text-stx-text text-sm font-medium flex items-center justify-center gap-1.5 hover:border-stx-accent hover:text-stx-accent transition-colors active:scale-[0.98]">
          <Sparkles size={14} /> KI-Anfrage
        </button>        <button data-testid="stx-detail-add-cart"
          onClick={handleAddToCart}
          disabled={addingToCart === true}
          className="h-11 px-4 rounded-full border border-stx-border text-stx-text text-sm font-medium flex items-center justify-center gap-1.5 hover:border-stx-accent hover:text-stx-accent transition-colors active:scale-[0.98] disabled:opacity-60">
          <ShoppingCart size={14} /> {addingToCart === "done" ? t("mp_detail.cart_added") : t("mp_detail.cart")}
        </button>
        <button data-testid="stx-detail-buy"
          onClick={() => {
            const params = new URLSearchParams({
              listing: listing.listing_id,
              area: "STILLTOUCHEDX",
            });
            if (selectedAddonCodes.length > 0) params.set("addons", selectedAddonCodes.join(","));
            if (selectedWearDays !== null && selectedWearDays !== undefined) params.set("wear_days", String(selectedWearDays));
            navigate(`/checkout?${params.toString()}`);
          }}
          className="flex-1 h-11 rounded-full bg-stx-accent text-stx-accent-fg text-sm font-semibold flex items-center justify-center gap-1.5 hover:bg-stx-accent-hover transition-colors active:scale-[0.98]">
          <ShoppingBag size={14} /> Kaufen
        </button>
          </>
        )}
      </div>

      {/* iter172l · Mehr vom Verkäufer — jetzt UNTER den CTA-Buttons (User-Vorgabe) */}
      {sellerMore.length > 0 && (
        <section data-testid="stx-detail-more-from-seller" className="max-w-3xl mx-auto px-4 md:px-6 mt-6 pt-6 border-t border-stx-border">
          <div className="flex items-center justify-between mb-3">
            <h3 className="text-[10px] font-bold text-stx-text-dim uppercase tracking-[0.2em]">{t("mp_detail.more_from_seller", { defaultValue: "Mehr vom Verkäufer" })}</h3>
            {listing.seller?.handle && (
              <button
                data-testid="stx-detail-more-from-seller-all"
                onClick={() => navigate(`/user/${listing.seller.handle}/stx`)}
                className="text-xs text-stx-accent hover:underline"
              >
                {t("mp_detail.view_all", { defaultValue: "Alle ansehen" })}
              </button>
            )}
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            {sellerMore.map((l) => (
              <button
                key={l.listing_id}
                data-testid={`stx-more-item-${l.listing_id}`}
                onClick={() => navigate(`/stilltouchedx/${l.listing_id}`)}
                className="block text-left group"
              >
                <div className="aspect-square rounded-xl overflow-hidden bg-stx-panel">
                  {l.media_urls?.[0] && (
                    <img src={l.media_urls[0]} alt={l.title || ""} loading="lazy" className="w-full h-full object-cover group-hover:scale-105 transition-transform" />
                  )}
                </div>
                <p className="mt-1.5 text-xs font-medium text-stx-text line-clamp-1">{l.title}</p>
                <p className="text-[11px] text-stx-text-muted">{l.price?.toFixed(2)} {l.currency}</p>
              </button>
            ))}
          </div>
        </section>
      )}
    </div>
  );
}
